import type { WeekStartDay } from "./types";

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_START_DAYS: Record<WeekStartDay, number> = {
  friday: 5,
  saturday: 6,
};
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

export const parseUtcDate = (value: string) => new Date(`${value}T00:00:00Z`);

export const formatDate = (value: Date) => value.toISOString().slice(0, 10);

export const formatShortDate = (value: Date) => SHORT_DATE_FORMATTER.format(value);

export const addDays = (value: Date, days: number) =>
  new Date(value.getTime() + days * DAY_MS);

export const getDatesInRange = (startDate: string, endDate: string) => {
  const start = parseUtcDate(startDate);
  const end = parseUtcDate(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return [];
  }
  if (end.getTime() <= start.getTime()) {
    return [formatDate(start)];
  }
  const dates: string[] = [];
  for (let current = start; current < end; current = addDays(current, 1)) {
    dates.push(formatDate(current));
  }
  return dates;
};

export const getIsoWeek = (value: Date) => {
  const date = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);
  return { week, weekYear: date.getUTCFullYear() };
};

export const getWeekInfo = (value: Date, weekStartDay: WeekStartDay) => {
  const startDay = WEEK_START_DAYS[weekStartDay];
  const dayOffset = (value.getUTCDay() - startDay + 7) % 7;
  const weekStartDate = addDays(value, -dayOffset);
  const weekYear = weekStartDate.getUTCFullYear();
  const yearStart = new Date(Date.UTC(weekYear, 0, 1));
  const yearStartOffset = (yearStart.getUTCDay() - startDay + 7) % 7;
  const firstWeekStart = addDays(yearStart, -yearStartOffset);
  const week = Math.floor((weekStartDate.getTime() - firstWeekStart.getTime()) / DAY_MS / 7) + 1;
  return { week, weekYear, startDate: weekStartDate };
};

export const getDatesInYear = (year: number) => {
  const dates: Date[] = [];
  for (
    let current = new Date(Date.UTC(year, 0, 1));
    current < new Date(Date.UTC(year + 1, 0, 1));
    current = addDays(current, 1)
  ) {
    dates.push(new Date(current));
  }
  return dates;
};

export const getMonthColor = (ratio: number) => {
  const clamped = Math.min(1, Math.max(0, ratio));
  const hue = Math.round(120 * clamped);
  const lightness = 92 - clamped * 20;
  return `hsl(${hue} 70% ${lightness}%)`;
};
